import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import { CONTRACT_STATUSES } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";

import { getAllProjects, createProject, updateProject, searchPartners, isAuthError, forceLogout, getUsersByIds, getSelectableUsers } from "../service/rpc";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";

const ITEMS_PER_PAGE = 6;

const EMPTY_FORM = {
  name: "", customerName: "", partner_id: "", budget: "",
  endDate: "", startDate: "", description: "", status: "Active",
  user_ids: [], contract_type: "", hourly_amount: ""
};

const STATUS_MAP = {
  'in_progress': 'Active', 'draft': 'Draft',
  'done': 'Completed', 'cancelled': 'Cancelled'
};

const STATUS_REVERSE_MAP = {
  'Active': 'in_progress', 'Draft': 'draft',
  'Completed': 'done', 'Cancelled': 'cancelled'
};

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const userFieldStyles = {
  "& .MuiOutlinedInput-root": {
    minHeight: "42px", alignItems: "flex-start",
    padding: "4px 8px", borderRadius: "10px",
    fontFamily: "'DM Sans', sans-serif", backgroundColor: "#f8fafc",
    "& fieldset": { borderColor: "#e2e8f0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#6b7fc4",
      boxShadow: "0 0 0 3px rgba(107, 127, 196, 0.12)",
    },
  },
  "& .MuiAutocomplete-input": {
    fontSize: "14px", color: "#1e293b",
    fontFamily: "'DM Sans', sans-serif",
  },
  "& .MuiAutocomplete-tag": { 
    margin: "4px 4px 0 0",
    height: "24px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#3b4b8f",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    "& .MuiChip-deleteIcon": {
      color: "#6b7fc4"
    }
  },
};

export default function ContractsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [contractsData, setContractsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating
  const [form, setForm] = useState(EMPTY_FORM);

  const lastSavedForm = useRef(null);
  const { partnerId, loading: userLoading, uid, name: userName, profileType } = useUser();

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter]);

  // Load selectable users
  useEffect(() => {
    if (userLoading) return;
    const loadUsers = async () => {
      try {
        const allUsers = await getSelectableUsers({ currentUserId: uid, currentUserName: userName });
        if (allUsers?.length > 0) setUsers(allUsers);
      } catch (e) { console.warn('Failed to load selectable users:', e.message); }
    };
    loadUsers();
  }, [uid, userName, userLoading]);

  // Fetch contracts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        let domain = [];
        if (search.trim()) {
          const q = search.trim();
          const searchDomain = ['|', ['name', 'ilike', q], ['partner_id', 'ilike', q]];
          domain = domain.length > 0 ? ['&', ...domain, ...searchDomain] : searchDomain;
        }
        const { projects, count } = await getAllProjects(domain, ITEMS_PER_PAGE, offset);
        const formatted = projects.map(p => ({
          id: p.id,
          name: p.name || "Unnamed Project",
          client: p.partner_id?.length > 1 ? p.partner_id[1] : "N/A",
          partner_id_raw: p.partner_id,
          status: STATUS_MAP[p.state] || "Draft",
          startDate: typeof p.date_start === 'string' ? p.date_start.split(' ')[0] : (p.date_start || "N/A"),
          endDate: typeof p.date_end === 'string' ? p.date_end.split(' ')[0] : (p.date_end || "N/A"),
          description: stripHtml(p.description) || "No description",
          budget: p.budget || 0,
          user_ids: Array.isArray(p.user_id) ? p.user_id.filter(v => typeof v === 'number') : [],
          contract_type: p.contract_type || "",
          hourly_amount: p.contract_type === 'hourly' ? p.hourly_amount :
            p.contract_type === 'fixed' ? p.fixed_amount :
              p.contract_type === 'monthly' ? p.monthly_amount :
                p.contract_type === 'weekly' ? p.weekly_amount : ""
        }));
        setContractsData(formatted);
        setTotalItems(count);

        // Resolve any user IDs not in list
        const allUserIds = [...new Set(formatted.flatMap(p => p.user_ids))];
        if (allUserIds.length > 0) {
          setUsers(prev => {
            const existingIds = new Set(prev.map(u => u.id));
            const missing = allUserIds.filter(id => !existingIds.has(id));
            if (missing.length === 0) return prev;
            getUsersByIds(missing).then(resolved => {
              if (resolved?.length > 0) {
                setUsers(current => {
                  const seen = new Map(current.map(u => [u.id, u]));
                  resolved.forEach(u => { if (!seen.has(u.id)) seen.set(u.id, u); });
                  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
                });
              }
            }).catch(() => {});
            return prev;
          });
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
        if (isAuthError(error)) { forceLogout(); return; }
      } finally {
        setLoading(false);
      }
    };
    if (!userLoading) fetchData();
  }, [search, currentPage, statusFilter, partnerId, uid, userLoading, userName]);

  // ── Modal helpers ──
  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    const initial = { ...EMPTY_FORM, customerName: userName || "", partner_id: partnerId || "", startDate: today };
    setForm(initial);
    lastSavedForm.current = initial;
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (e, contract) => {
    e.stopPropagation();
    const initial = {
      name: contract.name,
      budget: String(contract.budget || 0),
      startDate: contract.startDate === "N/A" ? "" : contract.startDate,
      endDate: contract.endDate === "N/A" ? "" : contract.endDate,
      description: contract.description || "",
      status: contract.status || "Draft",
      customerName: contract.client || "",
      partner_id: contract.partner_id_raw ? String(contract.partner_id_raw[0]) : "",
      user_ids: contract.user_ids || [],
      contract_type: contract.contract_type || "",
      hourly_amount: contract.hourly_amount ? String(contract.hourly_amount) : ""
    };
    setForm(initial);
    lastSavedForm.current = initial;
    setEditingId(contract.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUsersChange = (_, selectedUsers) => {
    setForm(prev => ({ ...prev, user_ids: selectedUsers.map(u => Number(u.id)) }));
  };

  const selectedUsers = users.filter(u => form.user_ids.includes(u.id));

  const renderUsersField = () => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={users}
      value={selectedUsers}
      onChange={handleUsersChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option?.name || ""}
      noOptionsText="No users found"
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Checkbox checked={selected} size="small"
              sx={{ mr: 1, color: "#94a3b8", "&.Mui-checked": { color: "#6b7fc4" } }} />
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => {
        const { renderTags, ...restParams } = params;
        return <TextField {...restParams} placeholder={selectedUsers.length === 0 ? "Select users" : ""} size="small" />;
      }}
      sx={userFieldStyles}
    />
  );

  const handleSave = async () => {
    const isCreating = editingId === null;
    const isDirty = JSON.stringify(form) !== JSON.stringify(lastSavedForm.current);
    if (!isDirty && !isCreating) return;

    try {
      setSaving(true);
      const payload = {
        name: form.name,
        partner_id: form.partner_id ? parseInt(form.partner_id) : (partnerId ? parseInt(partnerId) : false),
        date_start: (form.startDate && form.startDate !== "N/A") ? form.startDate : false,
        date_end: (form.endDate && form.endDate !== "N/A") ? form.endDate : false,
        description: form.description,
        state: STATUS_REVERSE_MAP[form.status] || 'draft',
        budget: parseFloat(form.budget) || 0,
        user_id: form.user_ids?.length > 0 ? [[6, 0, form.user_ids.map(Number)]] : [[6, 0, []]],
        contract_type: form.contract_type || false,
        hourly_amount: form.contract_type === 'hourly' ? (parseFloat(form.hourly_amount) || 0) : false,
        fixed_amount: form.contract_type === 'fixed' ? (parseFloat(form.hourly_amount) || 0) : false,
        monthly_amount: form.contract_type === 'monthly' ? (parseFloat(form.hourly_amount) || 0) : false,
        weekly_amount: form.contract_type === 'weekly' ? (parseFloat(form.hourly_amount) || 0) : false,
      };

      if (isCreating) {
        const newId = await createProject(payload);
        setContractsData(prev => [{
          id: newId, name: form.name,
          client: userName || "Your Profile",
          partner_id_raw: partnerId ? [partnerId, userName] : null,
          budget: parseFloat(form.budget) || 0,
          status: form.status,
          startDate: form.startDate || "N/A",
          endDate: form.endDate || "N/A",
          description: form.description,
          user_ids: [...form.user_ids],
          contract_type: form.contract_type,
          hourly_amount: form.hourly_amount
        }, ...prev]);
      } else {
        await updateProject(editingId, payload);
        setContractsData(prev => prev.map(c => c.id === editingId ? {
          ...c, name: form.name,
          budget: parseFloat(form.budget) || 0,
          status: form.status,
          startDate: form.startDate || c.startDate,
          endDate: form.endDate || c.endDate,
          description: form.description,
          user_ids: [...form.user_ids],
          contract_type: form.contract_type,
          hourly_amount: form.hourly_amount
        } : c));
      }

      lastSavedForm.current = { ...form };
      toast.success(isCreating ? "Contract created!" : "Saved!");
      closeModal();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const amountLabel = form.contract_type === 'fixed' ? 'Fixed Amount (₹)' :
    form.contract_type === 'hourly' ? 'Hourly Rate (₹)' :
      form.contract_type === 'monthly' ? 'Monthly Rate (₹)' :
        form.contract_type === 'weekly' ? 'Weekly Rate (₹)' : 'Amount (₹)';

  return (
    <main className="main">
      <style>{`
        .MuiAutocomplete-popper {
          z-index: 10000 !important;
        }
      `}</style>
      
      {/* ── CONTRACT MODAL (Create / Edit) ── */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingId ? "Edit Contract" : "New Contract"}
        onSave={handleSave}
        saving={saving}
        saveLabel={editingId ? "Save Changes" : "Create"}
      >
        <div className="modal-form">
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input
              className="form-input"
              name="name"
              value={form.name}
              onChange={handleFieldChange}
              placeholder="Enter project name..."
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input className="form-input" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Budget (₹)</label>
              <input className="form-input" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" name="status" value={form.status} onChange={handleFieldChange}>
                {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {profileType === 'consultant' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Contract Type</label>
                <select className="form-input" name="contract_type" value={form.contract_type} onChange={handleFieldChange}>
                  <option value="">— None —</option>
                  <option value="fixed">Fixed</option>
                  <option value="hourly">Hourly</option>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="form-group" style={{ opacity: !form.contract_type ? 0.55 : 1 }}>
                <label className="form-label">{amountLabel}</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  name="hourly_amount"
                  value={form.hourly_amount}
                  onChange={handleFieldChange}
                  placeholder="0.00"
                  disabled={!form.contract_type}
                  style={!form.contract_type ? { cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>
          )}

          {profileType === 'consultant' && (
            <div className="form-group">
              <label className="form-label">Assigned Users</label>
              {renderUsersField()}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              name="description"
              value={form.description}
              onChange={handleFieldChange}
              placeholder="Project description..."
              rows={3}
              style={{ minHeight: '80px' }}
            />
          </div>
        </div>
      </Modal>

      {/* ── PAGE HEADER ── */}
      <div className="page-header">
        <h1 className="page-header__title">Contracts</h1>
      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+ New</button>
        </div>
        <SearchBar search={search} setSearch={setSearch} placeholder="Search by name or client…" style={{ flex: 1, margin: 0 }} />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}

        {/* ── LIST VIEW ── */}
        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th">Project Name</th>
                <th className="table__th" style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {contractsData.length === 0 ? (
                <tr><td className="table__empty" colSpan={2}>No contracts match your filters.</td></tr>
              ) : contractsData.map((c) => (
                <tr key={c.id} className="table__row table__row--clickable" onClick={() => navigate(`/contracts/${c.id}`)}>
                  <td className="table__td">
                    <div className="contract-main-info">
                      <span className="table__name">{c.name}</span>
                      {profileType === 'consultant' && c.contract_type && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', textTransform: 'capitalize' }}>
                          {c.contract_type} • ₹{c.hourly_amount}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* ── KANBAN VIEW ── */
          <div className="task-grid">
            {contractsData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No contracts match your filters.</div>
            ) : contractsData.map((c) => (
              <div key={c.id} className="kanban-card" onClick={() => navigate(`/contracts/${c.id}`)}>
                <div className="kanban-card__top">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="kanban-card__title">{c.name}</span>
                    {profileType === 'consultant' && c.contract_type && (
                      <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'capitalize' }}>
                        {c.contract_type} • ₹{c.hourly_amount}
                      </span>
                    )}
                  </div>
                  <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
                <div className="kanban-card__bottom">
                  <div className="kanban-card__due">{c.startDate} → {c.endDate}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    </main>
  );
}
